import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/apiService';
import { showToast } from '../../components/Toast';
import './scorekeepingAdmin.css';

/**
 * Running the competition. A1 to A7.
 *
 * The test every screen here has to pass is that nothing becomes a monthly
 * ritual. Each job is either done once for a year, done at the moment a problem
 * is noticed, or not done at all. No screen here should need a competition
 * created by hand on the first of every month.
 *
 * Laid out for a phone, because excluding a machine happens on the floor next to
 * the machine, not at a desk. It scales up; it does not assume a desk.
 */

const TABS = [
  { key: 'months', label: 'Months' },
  { key: 'stern', label: 'Stern schedule' },
  { key: 'machines', label: 'Machines' },
  { key: 'sources', label: 'Score sources' },
  { key: 'settings', label: 'Settings' }
];

/** A year of months, which is the horizon the club plans Stern leaderboards over. */
const RUNWAY_PIPS = 12;

/** Add months to a YYYY-MM, staying in the club's own calendar terms. */
const addMonths = (period, n) => {
  const [y, m] = period.split('-').map(Number);
  const date = new Date(Date.UTC(y, (m - 1) + n, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
};

const periodLabelOf = (period) => {
  const [y, m] = period.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'long', year: 'numeric', timeZone: 'UTC'
  });
};

/** "1 Sep to 30 Sep", so a code can be checked against what was made in Stern. */
const monthRange = (period) => {
  const [y, m] = period.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const last = new Date(Date.UTC(y, m, 0));
  const fmt = (d) => d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${fmt(first)} to ${fmt(last)}`;
};

/** How long ago, in the words somebody would use out loud. */
const sinceWords = (value) => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `${Math.round(hours / 24)} days ago`;
};

const formatWhen = (value) => (value
  ? new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
  : '—');

const comparableTitle = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/\(.*?\)/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/**
 * The machine a Stern title most likely belongs to, to preselect in the picker.
 * Edition suffixes and punctuation are ignored, so "Godzilla" finds "Godzilla
 * (Premium)" and "Pokemon" finds "Pokémon (Pro)". Machines already linked to a
 * title are passed over. A guess only; the admin still presses Link.
 */
const guessMachineFor = (titleName, machines) => {
  const wanted = comparableTitle(titleName);
  if (!wanted) return null;
  const open = machines.filter((machine) => !machine.sternTitleName);
  return open.find((machine) => comparableTitle(machine.name) === wanted)
    || open.find((machine) => comparableTitle(machine.name).startsWith(wanted))
    || null;
};

function ScorekeepingAdmin() {
  const [tab, setTab] = useState('months');

  const [months, setMonths] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [machines, setMachines] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [settings, setSettings] = useState(null);

  const [limitDraft, setLimitDraft] = useState('');
  const [rotationDraft, setRotationDraft] = useState('');
  const [tickerDraft, setTickerDraft] = useState('');
  const [codesDraft, setCodesDraft] = useState('');
  const [batchOpen, setBatchOpen] = useState(false);
  const [excluding, setExcluding] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [excludePick, setExcludePick] = useState('');
  const [reReading, setReReading] = useState(null);
  const [reReadReason, setReReadReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [linkPicks, setLinkPicks] = useState({});

  const load = useCallback(async () => {
    try {
      const [m, sc, mm, un, st] = await Promise.all([
        apiService.get('/scorekeeping/months/admin'),
        apiService.get('/scorekeeping/stern/schedule'),
        apiService.get('/scorekeeping/machines'),
        apiService.get('/scorekeeping/stern/unmatched'),
        apiService.get('/scorekeeping/settings')
      ]);
      setMonths(m.data);
      setSchedule(sc.data);
      setMachines(mm.data.machines);
      setReasons(mm.data.reasons);
      setUnmatched(un.data);
      setSettings(st.data);
      setLimitDraft(String(st.data.countedMachineLimit));
      setRotationDraft(String(st.data.pageRotationSeconds));
      setTickerDraft(String(st.data.tickerSecondsPerItem));
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not load the competition', 'error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /*
   * What the runway card says, and in what colour.
   *
   * Three states rather than a number, because a number is only actionable to
   * somebody who already knows what a good number is. The wording changes at the
   * same points as the colour: comfortable, a job for this week, and a month that
   * is already losing automatic scores.
   */
  const runway = useMemo(() => {
    if (!schedule) return { tone: 'ok', headline: '', body: '' };
    const months = schedule.runwayMonths;

    if (schedule.missingCurrent) {
      return {
        tone: 'out',
        headline: `${periodLabelOf(schedule.currentPeriod).split(' ')[0]} has no leaderboard`,
        body: 'Automatic scores from Stern machines are not being recorded. Manual entry, the '
          + 'standings and the club screen all keep working, and scores already collected are safe. '
          + 'Stern keeps its own record, so registering one now and re-reading recovers what was missed.'
      };
    }

    const through = periodLabelOf(addMonths(schedule.currentPeriod, months - 1));
    if (months <= 2) {
      return {
        tone: 'low',
        headline: `${months} month${months === 1 ? '' : 's'} left`,
        body: `${through} is the last month with a leaderboard. Sitting down for twenty minutes `
          + 'some time in the next few weeks covers the rest of the year.'
      };
    }

    return {
      tone: months <= 3 ? 'low' : 'ok',
      headline: `${months} month${months === 1 ? '' : 's'} configured`,
      body: `Good until ${through}. You will be warned while there is still plenty of time to sit `
        + "down with Stern's site."
    };
  }, [schedule]);

  const countingMachines = useMemo(
    () => machines.filter((m) => m.countsTowardCompetition).length,
    [machines]
  );

  /* What actually gets recorded: the wording they picked, or what they typed
     when they picked Something else. */
  const excludeFinalReason = excludePick === 'Something else'
    ? excludeReason.trim()
    : excludePick;

  const limitChanged = Boolean(settings)
    && Number(limitDraft) !== Number(settings.countedMachineLimit);

  const currentMonth = useMemo(
    () => months.find((m) => m.isCurrent) || null,
    [months]
  );

  /*
   * Months between the end of the runway and the end of the year, which is what
   * "Missing" means on the list. Without these the list stops at the last month
   * that happens to be configured, and a gap looks the same as the end.
   */
  const gapPeriods = useMemo(() => {
    if (!schedule) return [];
    const known = new Set(schedule.entries.map((e) => e.period));
    const out = [];
    let cursor = schedule.currentPeriod;
    for (let i = 0; i < RUNWAY_PIPS; i++) {
      if (!known.has(cursor)) out.push(cursor);
      cursor = addMonths(cursor, 1);
    }
    return out;
  }, [schedule]);

  /*
   * Which month each pasted code would land on, shown before anything is saved.
   * Pasting a year of codes into a box and pressing a button is a lot of trust to
   * ask for; the assignment is the part that can be wrong, so it is the part that
   * gets checked.
   */
  const batchAssignment = useMemo(() => {
    if (!schedule) return [];
    const codes = codesDraft.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const known = new Set(schedule.entries.map((e) => e.period));
    const out = [];
    let cursor = schedule.currentPeriod;
    for (const code of codes) {
      while (known.has(cursor)) cursor = addMonths(cursor, 1);
      out.push({ code, period: cursor, label: periodLabelOf(cursor) });
      known.add(cursor);
    }
    return out;
  }, [codesDraft, schedule]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      await apiService.put('/scorekeeping/settings', {
        countedMachineLimit: parseInt(limitDraft, 10),
        pageRotationSeconds: parseInt(rotationDraft, 10),
        tickerSecondsPerItem: parseInt(tickerDraft, 10)
      });
      showToast('Settings saved. This affects the open month only.', 'success');
      await load();
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not save', 'error');
    } finally { setBusy(false); }
  };

  const registerCodes = async () => {
    if (batchAssignment.length === 0) return;

    // Exactly what the admin was shown, rather than a second calculation of it.
    // Bulk is the primary flow: a year of leaderboards is made in Stern in one
    // sitting and the codes come here together.
    const entries = batchAssignment.map(({ period, code }) => ({ period, eventCode: code }));

    setBusy(true);
    try {
      const { data } = await apiService.post('/scorekeeping/stern/schedule', { entries });
      showToast(`Registered ${data.registered.length} leaderboard${data.registered.length === 1 ? '' : 's'}`, 'success');
      setCodesDraft('');
      setBatchOpen(false);
      await load();
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not register those', 'error');
    } finally { setBusy(false); }
  };

  const applyCounting = async (machine, counts, reason) => {
    setBusy(true);
    try {
      const { data } = await apiService.patch(`/scorekeeping/machines/${machine.id}/counting`, { counts, reason });
      showToast(
        counts
          ? `${data.name} counts again`
          : `${data.name} is out of the competition. ${data.scoresAffected} score${data.scoresAffected === 1 ? '' : 's'} stay visible but earn nothing.`,
        'success'
      );
      await load();
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not update that machine', 'error');
    } finally {
      setBusy(false);
      setExcluding(null);
      setExcludeReason('');
      setExcludePick('');
    }
  };

  const doReRead = async () => {
    setBusy(true);
    try {
      const { data } = await apiService.post(`/scorekeeping/stern/re-read/${reReading.period}`, { reason: reReadReason });
      showToast(
        data.reSettled
          ? `${reReading.label} re-settled. ${data.added} score(s) changed, badges reconciled.`
          : `Nothing changed in ${reReading.label}.`,
        'success'
      );
      await load();
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not re-read that month', 'error');
    } finally {
      setBusy(false);
      setReReading(null);
      setReReadReason('');
    }
  };

  // Links the title, then reads Stern straight away so the held scores reach the
  // board now rather than at the next scheduled read.
  const linkTitle = async (title, machineId) => {
    setBusy(true);
    try {
      const { data } = await apiService.post('/scorekeeping/stern/unmatched/link', {
        titleName: title.titleName,
        machineId
      });
      try {
        await apiService.post('/scorekeeping/stern/read', {});
        showToast(`Linked "${data.titleName}" to ${data.machineName}. Its scores are on the board.`, 'success');
      } catch (readError) {
        showToast(
          `Linked "${data.titleName}" to ${data.machineName}. Its scores arrive at the next Stern read.`,
          'success'
        );
      }
      setLinkPicks((prev) => {
        const next = { ...prev };
        delete next[title.id];
        return next;
      });
      await load();
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not link that title', 'error');
    } finally { setBusy(false); }
  };

  const readSternNow = async () => {
    setBusy(true);
    try {
      const { data } = await apiService.post('/scorekeeping/stern/read', {});
      showToast(
        data.ingested
          ? `Read Stern: ${data.added} new or improved, ${data.unchanged} unchanged`
          : `Nothing read (${data.reason}). Standings and manual entry are unaffected.`,
        data.ingested ? 'success' : 'error'
      );
      await load();
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not read Stern', 'error');
    } finally { setBusy(false); }
  };

  if (!schedule || !settings) return <div className="ska-page"><p className="ska-muted">Loading…</p></div>;

  return (
    <div className="ska-page">
      <header className="ska-header">
        <div className="ska-header__top">
          <h1>Running the competition</h1>
          {/* A new tab on purpose. It renders as bare signage with no navigation,
              which is right on the TV in the club and a dead end in the tab an
              admin was working in. */}
          <a
            className="ska-screenlink"
            href="/scorekeeping/tv"
            target="_blank"
            rel="noopener noreferrer"
          >
            Club screen
            <span aria-hidden="true">&#8599;</span>
          </a>
        </div>
        <p className="ska-muted">
          Nothing here needs doing on the first of the month. A month closes when it ends.
        </p>
      </header>

      <nav className="ska-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`ska-tab ${tab === t.key ? 'is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'stern' && schedule.needsAttention && <span className="ska-tab__flag" />}
            {t.key === 'sources' && unmatched.length > 0 && <span className="ska-tab__flag" />}
          </button>
        ))}
      </nav>

      {tab === 'months' && (
        <section className="ska-section">
          {months.map((month) => (
            <article className={`ska-card ${month.status === 'open' ? 'is-open' : ''}`} key={month.period}>
              <div className="ska-card__head">
                <h2>{month.label}</h2>
                <span className={`ska-badge ska-badge--${month.status === 'open' ? 'open' : 'settled'}`}>
                  {month.status === 'open' ? 'Open' : 'Settled'}
                </span>
              </div>
              <p className="ska-muted">
                {month.scoreCount} score{month.scoreCount === 1 ? '' : 's'} across {month.machineCount} machine
                {month.machineCount === 1 ? '' : 's'}
                {month.settledAt ? ` · settled ${formatWhen(month.settledAt)}` : ''}
              </p>
              {month.status === 'settled' && (
                <button
                  type="button"
                  className="ska-btn ska-btn--quiet"
                  onClick={() => setReReading(month)}
                >
                  Re-read from Stern
                </button>
              )}
            </article>
          ))}
        </section>
      )}

      {tab === 'stern' && (
        <section className="ska-section">
          {/* Runway, as a thing you glance at rather than read.
              The number alone ("7") does not tell an admin whether to act. The
              pips do: a row that is mostly full is fine, a row that is mostly
              empty is a job for this week, and the state changes colour before it
              changes wording. */}
          <article className={`ska-runway is-${runway.tone}`}>
            <p className="ska-runway__eyebrow">
              Runway{runway.tone === 'ok' ? '' : ` \u00b7 ${runway.tone === 'low' ? 'running low' : 'exhausted'}`}
            </p>
            <p className="ska-runway__headline">{runway.headline}</p>

            <div className="ska-pips" role="img" aria-label={`${schedule.runwayMonths} of ${RUNWAY_PIPS} months configured`}>
              {Array.from({ length: RUNWAY_PIPS }, (_, i) => (
                <span key={i} className={`ska-pip ${i < schedule.runwayMonths ? 'is-on' : ''}`} />
              ))}
            </div>

            <p className="ska-runway__body">{runway.body}</p>

            <button type="button" className="ska-btn" onClick={() => setBatchOpen(true)}>
              Add a batch of codes
            </button>
          </article>

          {/* What is happening right now, which is the other half of "is this
              working". A registered code with nothing arriving looks exactly like
              a registered code that is fine, until you can see when it was last
              read and how much has come in. */}
          <article className="ska-card">
            <p className="ska-eyebrow">{schedule.missingCurrent ? 'Not configured' : 'Active now'}</p>
            <h2>{currentMonth?.label || schedule.currentPeriod}</h2>

            {schedule.missingCurrent ? (
              <p className="ska-warn">
                No leaderboard is registered, so no automatic scores are arriving. Manual entry, the
                standings and the club screen all keep working, and Stern keeps its own record, so
                registering one now and re-reading recovers what was missed.
              </p>
            ) : (
              <>
                <p className="ska-code ska-code--lead">{schedule.currentEventCode}</p>
                <dl className="ska-facts">
                  <div>
                    <dt>Last read</dt>
                    <dd>{schedule.currentLastReadAt ? sinceWords(schedule.currentLastReadAt) : 'Never'}</dd>
                  </div>
                  <div>
                    <dt>Scores this month</dt>
                    <dd>
                      {currentMonth
                        ? `${currentMonth.scoreCount} from ${currentMonth.machineCount} machines`
                        : 'None yet'}
                    </dd>
                  </div>
                </dl>
              </>
            )}

            <button type="button" className="ska-btn ska-btn--quiet" onClick={readSternNow} disabled={busy}>
              Read Stern now
            </button>
          </article>

          <article className="ska-card">
            <p className="ska-eyebrow">Every month</p>
            <ul className="ska-months">
              {schedule.entries.map((entry) => (
                <li key={entry.period} className={entry.isCurrent ? 'is-current' : ''}>
                  <div className="ska-months__who">
                    <span className="ska-months__label">{entry.label}</span>
                    <span className="ska-code">{entry.eventCode}</span>
                    <span className="ska-months__range">{monthRange(entry.period)}</span>
                  </div>
                  <span className={`ska-state ska-state--${entry.isCurrent ? 'active' : entry.isPast ? 'past' : 'ready'}`}>
                    {entry.isCurrent ? 'Active' : entry.isPast ? 'Closed' : 'Ready'}
                  </span>
                </li>
              ))}
              {gapPeriods.map((period) => (
                <li key={period} className="is-missing">
                  <div className="ska-months__who">
                    <span className="ska-months__label">{periodLabelOf(period)}</span>
                    <span className="ska-code ska-code--missing">Not configured</span>
                    <span className="ska-months__range">{monthRange(period)}</span>
                  </div>
                  <span className="ska-state ska-state--missing">Missing</span>
                </li>
              ))}
            </ul>
          </article>
        </section>
      )}

      {tab === 'machines' && (
        <section className="ska-section">
          <p className="ska-muted ska-section__lead">
            Every machine counts unless somebody turns it off, and it stays off until somebody turns
            it back on. It does not reset in a new month and nothing will remind you, which is the
            point. Condition is a separate thing and does not affect the competition.
          </p>
          {machines.map((machine) => (
            <article className={`ska-row ${machine.countsTowardCompetition ? '' : 'is-excluded'}`} key={machine.id}>
              <div className="ska-row__main">
                <p className="ska-row__name">{machine.name}</p>
                <p className="ska-muted">
                  {machine.countsTowardCompetition
                    ? `Counting · ${machine.scoreCount} score${machine.scoreCount === 1 ? '' : 's'} this month`
                    : `Not counting since ${formatWhen(machine.countingChangedAt)}`}
                </p>
                {!machine.countsTowardCompetition && machine.countingReason && (
                  <p className="ska-row__reason">
                    {machine.countingReason}
                    {machine.countingChangedBy ? ` — ${machine.countingChangedBy}` : ''}
                  </p>
                )}
              </div>
              <button
                type="button"
                className={`ska-toggle ${machine.countsTowardCompetition ? 'is-on' : ''}`}
                onClick={() => (machine.countsTowardCompetition
                  ? setExcluding(machine)
                  : applyCounting(machine, true, null))}
                disabled={busy}
              >
                {machine.countsTowardCompetition ? 'Counts' : 'Excluded'}
              </button>
            </article>
          ))}
        </section>
      )}

      {tab === 'sources' && (
        <section className="ska-section">
          <article className="ska-card">
            <h2>Scores arriving that match no machine</h2>
            <p className="ska-muted">
              Nothing is discarded. These are held and attach themselves as soon as a machine claims
              the title. Pick the machine a title belongs to and its scores land on the board.
            </p>
            {unmatched.length === 0 ? (
              <p className="ska-muted">Everything arriving from Stern is landing on a machine.</p>
            ) : (
              unmatched.map((title) => {
                const picked = linkPicks[title.id] ?? guessMachineFor(title.titleName, machines)?.id ?? '';
                return (
                  <div className="ska-row ska-row--link" key={title.id}>
                    <div className="ska-row__main">
                      <p className="ska-row__name">{title.titleName}</p>
                      <p className="ska-muted">
                        {title.entryCount} score{title.entryCount === 1 ? '' : 's'} in {title.period}
                        {' · newest '}{formatWhen(title.lastSeenAt)}
                      </p>
                    </div>
                    {/* A new machine's name in the app rarely matches Stern's title
                        exactly ("Godzilla (Premium)" against "Godzilla"), and without
                        this the only way to link one was a hand-made API call. */}
                    <div className="ska-link">
                      <select
                        className="ska-input"
                        aria-label={`Machine for ${title.titleName}`}
                        value={picked}
                        onChange={(e) => setLinkPicks((prev) => ({ ...prev, [title.id]: e.target.value }))}
                      >
                        <option value="">Choose a machine</option>
                        {[...machines].sort((a, b) => a.name.localeCompare(b.name)).map((machine) => (
                          <option key={machine.id} value={machine.id}>{machine.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="ska-btn"
                        onClick={() => linkTitle(title, picked)}
                        disabled={busy || !picked}
                      >
                        Link
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </article>

          <article className="ska-card">
            <h2>Machines that report through Stern</h2>
            <table className="ska-table">
              <tbody>
                {machines.filter((m) => m.hasInsiderConnected).map((machine) => (
                  <tr key={machine.id}>
                    <td>{machine.name}</td>
                    <td className="ska-code">{machine.sternTitleName || machine.name}</td>
                    <td className="ska-right">
                      <span className={`ska-badge ska-badge--${machine.sternTitleName ? 'ok' : 'proposed'}`}>
                        {machine.sternTitleName ? 'Linked' : 'By name'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="ska-muted">
              A machine with no explicit link is matched on its own name, so a new machine starts
              collecting scores without anybody configuring anything.
            </p>
          </article>
        </section>
      )}

      {tab === 'settings' && (
        <section className="ska-section">
          <article className="ska-card">
            <p className="ska-eyebrow">Competition settings</p>
            <h2>Machines counting toward a monthly total</h2>
            <p className="ska-muted">
              A player&apos;s total is the sum of their best this many machines. Lower favours
              specialists, who can collect the games they are strong at. Higher favours breadth, and
              rewards playing everything on the floor.
            </p>

            {/* A stepper, not a number field. This is one number that moves by one
                or two when it moves at all, and a keyboard on a phone is a poor
                way to nudge it. */}
            <div className="ska-stepper">
              <button
                type="button"
                aria-label="One fewer machine"
                onClick={() => setLimitDraft(String(Math.max(1, Number(limitDraft || 0) - 1)))}
              >
                &#8722;
              </button>
              <span className="ska-stepper__value">{limitDraft}</span>
              <button
                type="button"
                aria-label="One more machine"
                onClick={() => setLimitDraft(String(Math.max(1, Number(limitDraft || 0) + 1)))}
              >
                +
              </button>
            </div>
            <p className="ska-muted ska-stepper__note">
              Out of {countingMachines} counting machines on the floor today.
            </p>

            {/* The consequence, stated before the save rather than discovered
                after it. What makes this safe to change mid-month is that closed
                months keep their own number, and that is the thing an admin needs
                to be sure of. */}
            {limitChanged && (
              <div className="ska-note">
                <strong>Changing this now affects {currentMonth?.label || 'the open month'} only</strong>
                <span>
                  Its standings recalculate immediately. Every earlier month keeps the number that
                  was in force when it closed, so past results never move.
                </span>
              </div>
            )}

            <button type="button" className="ska-btn" onClick={saveSettings} disabled={busy}>
              {limitChanged ? `Save for ${currentMonth?.label?.split(' ')[0] || 'the open month'}` : 'Save'}
            </button>

            <p className="ska-eyebrow ska-spaced">What each month was settled with</p>
            <ul className="ska-settled">
              {months.map((m) => (
                <li key={m.period}>
                  <span className="ska-settled__who">
                    <span className="ska-settled__label">{m.label}</span>
                    <span className="ska-settled__status">{m.status}</span>
                  </span>
                  <span className="ska-settled__value">
                    {m.status === 'settled'
                      ? (m.countedMachineLimit ?? '\u2014')
                      : (settings?.countedMachineLimit ?? '\u2014')}
                  </span>
                </li>
              ))}
            </ul>
            <p className="ska-muted">
              Stored per month as a consequence of settling it, not maintained by hand.
            </p>
          </article>

          <article className="ska-card">
            <p className="ska-eyebrow">Club screen</p>
            <h2>Page rotation</h2>
            <p className="ska-muted">Seconds each page of machines holds before the next.</p>
            <input
              className="ska-input"
              type="number"
              min="1"
              value={rotationDraft}
              onChange={(e) => setRotationDraft(e.target.value)}
            />

            <h2 className="ska-spaced">Ticker speed</h2>
            <p className="ska-muted">
              Seconds each score takes to cross the screen. Set per score rather than per loop, so
              the speed stays the same whether the ticker holds three or twenty. Higher is slower.
            </p>
            <input
              className="ska-input"
              type="number"
              min="1"
              value={tickerDraft}
              onChange={(e) => setTickerDraft(e.target.value)}
            />

            <button type="button" className="ska-btn" onClick={saveSettings} disabled={busy}>
              Save
            </button>
          </article>
        </section>
      )}

      {excluding && (
        <div className="ska-sheet" role="dialog" aria-label="Take a machine out of the competition">
          <div className="ska-sheet__panel">
            <h2>Take it out of the competition?</h2>
            {/* Says what happens to the scores that already exist, because that is
                the fear this dialog has to answer, and names the month that moves
                so nobody wonders whether history moved too. */}
            <p className="ska-muted">
              Scores already recorded on {excluding.name} stay on its own board. They stop earning
              points from now on. {currentMonth?.label || 'The open month'}&apos;s totals recalculate.
            </p>

            <p className="ska-eyebrow">Why</p>
            {/* Full-width choices rather than pills. There are three, they are
                sentences rather than labels, and a wrapped row of sentence-length
                pills is a paragraph with borders in it. */}
            <div className="ska-choices">
              {reasons.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  className={`ska-choice ${excludePick === reason ? 'is-picked' : ''}`}
                  aria-pressed={excludePick === reason}
                  onClick={() => { setExcludePick(reason); setExcludeReason(''); }}
                >
                  {reason}
                </button>
              ))}
            </div>

            {/* Only when there is something to type. An always-present box asked
                for the reason a second time, having just been given one. */}
            {excludePick === 'Something else' && (
              <input
                className="ska-input"
                placeholder="Why, in your own words"
                value={excludeReason}
                onChange={(e) => setExcludeReason(e.target.value)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            )}

            <div className="ska-sheet__actions ska-sheet__actions--split">
              <button
                type="button"
                className="ska-btn ska-btn--quiet"
                onClick={() => { setExcluding(null); setExcludeReason(''); setExcludePick(''); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ska-btn ska-btn--danger"
                disabled={!excludeFinalReason || busy}
                onClick={() => applyCounting(excluding, false, excludeFinalReason)}
              >
                Stop it counting
              </button>
            </div>
          </div>
        </div>
      )}

      {reReading && (
        <div className="ska-sheet" role="dialog" aria-label="Re-read a settled month">
          <div className="ska-sheet__panel">
            <h2>Re-read {reReading.label} from Stern?</h2>
            <p className="ska-muted">
              This is the only way a settled month ever changes. It can move the final standings,
              members&apos; recorded finishes, and the badges awarded for that month, which may mean
              taking one back from somebody who was told they had won it.
            </p>
            <input
              className="ska-input"
              placeholder="Reason, recorded permanently"
              value={reReadReason}
              onChange={(e) => setReReadReason(e.target.value)}
            />
            <div className="ska-sheet__actions">
              <button
                type="button"
                className="ska-btn ska-btn--quiet"
                onClick={() => { setReReading(null); setReReadReason(''); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ska-btn ska-btn--danger"
                disabled={!reReadReason.trim() || busy}
                onClick={doReRead}
              >
                Re-read and re-settle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The same sheet the other decision panels on this page use, rather than a
          second one of my own. I introduced one and reused its class name, so the
          two definitions of .ska-sheet fought and this dialog laid itself out as
          a row. */}
      {batchOpen && (
        <div className="ska-sheet" role="dialog" aria-label="Add a batch of codes">
          <div className="ska-sheet__panel">
            <h2>Add a batch of codes</h2>
            <p className="ska-muted">
              Create the leaderboards in Stern first, one per month with that month&apos;s start and
              end dates. Then paste the codes here, one per line, oldest first.
            </p>

            <textarea
              className="ska-textarea"
              rows={6}
              value={codesDraft}
              onChange={(e) => setCodesDraft(e.target.value)}
              placeholder={'lb_2c81aa\nlb_9d3f70\nlb_51b8e2'}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />

            {/* The assignment, before anything is saved. Pasting a year of codes
                into a box and pressing a button is a lot of trust to ask for, and
                which code lands on which month is the part that can be wrong. */}
            {batchAssignment.length > 0 && (
              <>
                <p className="ska-eyebrow">Will be assigned to</p>
                <ul className="ska-assign">
                  {batchAssignment.map(({ code, period, label }) => (
                    <li key={period}>
                      <span className="ska-code">{code}</span>
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
                <p className="ska-muted ska-assign__note">
                  Assigned to the next unconfigured months in order. Months that already have a
                  leaderboard are skipped rather than overwritten.
                </p>
              </>
            )}

            <div className="ska-sheet__actions">
              <button type="button" className="ska-btn ska-btn--quiet" onClick={() => setBatchOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="ska-btn"
                disabled={batchAssignment.length === 0 || busy}
                onClick={registerCodes}
              >
                Register {batchAssignment.length || ''} leaderboard{batchAssignment.length === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default ScorekeepingAdmin;
